import { cn } from '@/lib/utils';
import iconSrc from '/brand/asset-hound-icon.png';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// Figma renders the Asset Hound wordmark as a horizontal lockup: square icon
// on the left, "asset" / "hound" stacked text on the right, all in cyan
// `#6FADD2`. Mirrored here using the cropped icon PNG + code text so we keep
// the colour version of the icon while matching Figma's layout exactly.
const LOGO_COLOR = '#6FADD2';

const sizes = {
  // Figma top-nav lockup is 118×50.
  sm: {
    iconClass: 'h-12 w-12',
    fontSize: '15px',
    lineHeight: '17px',
    gap: '8px',
    fontWeight: 700,
  },
  // Figma form lockup is 158.248×67.047. Icon ~67×67, text ~83 wide.
  md: {
    iconClass: 'h-[67px] w-[67px]',
    fontSize: '22px',
    lineHeight: '24px',
    gap: '10px',
    fontWeight: 700,
  },
  // Larger general-purpose size (also matches the existing test-suite class).
  lg: {
    iconClass: 'h-36 w-36',
    fontSize: '40px',
    lineHeight: '44px',
    gap: '16px',
    fontWeight: 700,
  },
} as const;

export function Logo({ size = 'md', className }: LogoProps) {
  const cfg = sizes[size];
  return (
    <span
      className={cn('inline-flex select-none items-center', className)}
      style={{ gap: cfg.gap }}
    >
      <img
        src={iconSrc}
        alt="Asset Hound"
        className={cn('aspect-square flex-none object-contain', cfg.iconClass)}
        draggable={false}
      />
      <span
        className="flex flex-col"
        style={{
          color: LOGO_COLOR,
          fontFamily: 'Inter, sans-serif',
          fontWeight: cfg.fontWeight,
          fontSize: cfg.fontSize,
          lineHeight: cfg.lineHeight,
        }}
        aria-hidden
      >
        <span>asset</span>
        <span>hound</span>
      </span>
    </span>
  );
}
